import { backofficeFetch } from '@/services/backoffice-api';
import type {
  BackofficeVoucherDetailResponse,
  BackofficeVoucherRedemptionsResponse,
  BackofficeVouchersResponse,
  CreateBackofficeVoucherInput,
  CreateBackofficeVoucherResponse,
  UpdateBackofficeVoucherInput,
  UpdateBackofficeVoucherResponse,
} from './types';

export async function fetchBackofficeVouchers(
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeVouchersResponse> {
  const response = await backofficeFetch(`/vouchers?${searchParams.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new Error(`Failed to load vouchers (${response.status})`);
  }

  return (await response.json()) as BackofficeVouchersResponse;
}

export async function fetchBackofficeVoucherDetail(
  voucherId: string,
  signal?: AbortSignal
): Promise<BackofficeVoucherDetailResponse> {
  const response = await backofficeFetch(`/vouchers/${encodeURIComponent(voucherId)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new Error(`Failed to load voucher (${response.status})`);
  }

  return (await response.json()) as BackofficeVoucherDetailResponse;
}

export async function fetchBackofficeVoucherRedemptions(
  voucherId: string,
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeVoucherRedemptionsResponse> {
  const response = await backofficeFetch(
    `/vouchers/${encodeURIComponent(voucherId)}/redemptions?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json' },
      ...(signal ? { signal } : {}),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to load voucher redemptions (${response.status})`);
  }

  return (await response.json()) as BackofficeVoucherRedemptionsResponse;
}

export async function createBackofficeVoucher(
  input: CreateBackofficeVoucherInput
): Promise<CreateBackofficeVoucherResponse> {
  const response = await backofficeFetch('/vouchers', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to create voucher (${response.status})`);
  }

  return body as CreateBackofficeVoucherResponse;
}

export async function updateBackofficeVoucher(
  voucherId: string,
  input: UpdateBackofficeVoucherInput
): Promise<UpdateBackofficeVoucherResponse> {
  const response = await backofficeFetch(`/vouchers/${encodeURIComponent(voucherId)}`, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  const body = (await response.json().catch(() => null)) as { error?: string } | null;

  if (!response.ok) {
    throw new Error(body?.error ?? `Failed to update voucher (${response.status})`);
  }

  return body as UpdateBackofficeVoucherResponse;
}
